// Catalog.ID Client — In-Memory Key State
// ============================================================================
// Holds decrypted private keys for the current session.
// Keys are NEVER written to disk — only to memory.
// Cleared on logout or tab close.
//
// Audit M12: the supersecret-derived AES key is intentionally NOT cached
// here. Mutating the keybag is a rare, security-relevant action; we
// re-prompt for the supersecret each time and zero the derived key on
// use. See key-grant/supersecret-prompt.ts and the bag writers that call it
// (derive-root-store, recovery-kit-store, ceremony-keystore).

import type { KeyBag, KeyBagEntry } from '../crypto/keybag';
import { fromBase64 } from '../crypto/aes';

class KeyState {
    private keyBag: KeyBag | null = null;
    /**
     * Resolvers queued by handlers that are waiting for the keybag to be
     * unlocked. Each call to {@link load} resolves all of them and clears the
     * list. Since M2c (cid-encrypted-storage §10 item 5) EVERY cross-module
     * handler waits here, whatever the sender's `requiresUserInteraction` flag
     * says: `key-grant/unlock-gate.ts` first asks the hub to raise the CID tab
     * (`focus-request`) so there is a login screen for the member to answer,
     * then races this promise against the shared five-minute budget. Two
     * deliberate exceptions read {@link isLoaded} directly — chat's
     * `get-claims`, a background roster refresh that degrades to the public
     * half rather than steal focus, and `on-demand-key.ts`, which is only
     * reached after a gate has already run.
     */
    private loadWaiters: Array<() => void> = [];

    /** Load a decrypted key bag into memory. Resolves any pending
     *  {@link waitForLoad} promises. */
    load(bag: KeyBag): void {
        this.keyBag = bag;
        const waiters = this.loadWaiters;
        this.loadWaiters = [];
        for (const resolve of waiters) {
            try { resolve(); } catch { /* swallow — waiter promise chain handles its own errors */ }
        }
    }

    /** Clear all keys from memory. Does NOT reject pending waiters — those
     *  remain queued until the next {@link load} or until they time out on
     *  their own. */
    clear(): void {
        this.keyBag = null;
    }

    /** Whether keys are loaded. */
    isLoaded(): boolean {
        return this.keyBag !== null;
    }

    /**
     * Returns a promise that resolves the next time the keybag is loaded
     * (i.e. on the next {@link load} call). If the keybag is already loaded
     * the promise resolves immediately. Caller is responsible for combining
     * this with a timeout — the waiter list has no built-in TTL.
     */
    waitForLoad(): Promise<void> {
        if (this.keyBag !== null) return Promise.resolve();
        return new Promise<void>(resolve => {
            this.loadWaiters.push(resolve);
        });
    }

    /** Get the full key bag. */
    getKeyBag(): KeyBag | null {
        return this.keyBag;
    }

    /**
     * Append one freshly-minted entry to the in-memory bag.
     *
     * Exists for the A8b ceremony flow: a rotation generates its incoming
     * authority key, persists it server-side, and must then SIGN with it in the
     * same round trip. Without this the key would only become usable after a
     * re-login, and a rotation interrupted between the two would strand the
     * stream — no successor survives a rotation (asset-ownership plan D8), so
     * there is no other recovery path.
     *
     * Callers MUST have persisted the entry to the server first. This is the
     * memory-side mirror of that write, never a substitute for it: a key that
     * exists only here is lost on tab close.
     *
     * A no-op on a locked bag — there is nothing to append to, and creating a
     * bag here would fabricate an unlocked state nobody authenticated.
     * Replaces an existing (keyId, keyIndex) rather than duplicating it, so a
     * retried persist stays idempotent.
     */
    addEntry(entry: KeyBagEntry): void {
        if (!this.keyBag) return;
        const index = entry.keyIndex ?? 0;
        const at = this.keyBag.entries.findIndex(
            e => e.keyId === entry.keyId && (e.keyIndex ?? 0) === index);
        if (at >= 0) this.keyBag.entries[at] = entry;
        else this.keyBag.entries.push(entry);
    }

    /** Get the active private key by key ID. Returns base64-encoded key or null. */
    getPrivateKey(keyId: string): string | null {
        if (!this.keyBag) return null;
        // Prefer active entry; fall back to any entry with this keyId
        const active = this.keyBag.entries.find(e => e.keyId === keyId && e.status !== 'revoked');
        return active?.privateKey ?? this.keyBag.entries.find(e => e.keyId === keyId)?.privateKey ?? null;
    }

    /** Check if a specific key is available. */
    hasKey(keyId: string): boolean {
        return this.getPrivateKey(keyId) !== null;
    }

    /**
     * Get all versions of a key for chain-walking decryption after key rotation.
     * Returns entries sorted by keyIndex ascending, with secretKey as Uint8Array.
     */
    getKeyChain(keyId: string): { secretKey: Uint8Array; keyIndex: number }[] {
        if (!this.keyBag) return [];
        return this.keyBag.entries
            .filter(e => e.keyId === keyId)
            .sort((a, b) => (a.keyIndex ?? 0) - (b.keyIndex ?? 0))
            .map(e => ({
                secretKey: fromBase64(e.privateKey),
                keyIndex: e.keyIndex ?? 0,
            }));
    }
}

export const keys = new KeyState();
